import { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { getUserEducation } from '../../../../../actions/licensesAction';
import { createLoadingSelector } from '../../../../../apis/selectors';
import { UserType } from '../../../../../enums/UserType';
import { TabViewContainer } from '../../../../commonComponents/TabViewContainer';
import { JobStatus } from '../../shared/constants';
import { CompensationBenefitsCard } from './components/CompensationBenefitsCard';
import { ConfirmCloseJobPopup } from './components/ConfirmCloseJobPopup';
import { GeneralInformationCard } from './components/GeneralInformationCard';
import { JobDescriptionCard } from './components/JobDescriptionCard';
import { JobDetailsCard } from './components/JobDetailsCard';
import { ScheduleInformationCard } from './components/ScheduleInformationCard';
import { useEditPostingForm } from './hooks/useEditPostingForm';
import { useJobDetails } from './hooks/useJobDetails';
import './styles.scss';

const loadingSelector = createLoadingSelector(['GET_ALL_PROFESSIONS', 'GET_USER_EDUCATION']);

export const JobInformationScreen = () => {
  const dispatch = useDispatch();
  const match = useRouteMatch();

  const isInitialLoading = useSelector((state) => loadingSelector(state));

  const [showConfirmCloseJobPopup, setShowConfirmCloseJobPopup] = useState(false);

  const { job, isLoading, } = useJobDetails(match.params.jobId);

  const {
    formEdits,
    formValues,
    isPending: isUpdateJobPostingPending,
    doUpdateJobPosting,
    doUpdateRecruiter,
    doUpdateStatus,
    handleFormChange,
  } = useEditPostingForm({
    currentJobPosting: job,
  });

  useEffect(() => {
    dispatch(getUserEducation({ userType: UserType.DNT }));
  }, [dispatch]);

  const handleSaveClick = useCallback(() => {
    doUpdateJobPosting({
      ...formValues,
      postingId: match.params.jobId,
    });
  }, [doUpdateJobPosting, formValues, match.params.jobId]);

  const onJobStatusChange = (_, status) => {
    if (status === JobStatus.CLOSED) {
      setShowConfirmCloseJobPopup(true);
      return;
    }

    doUpdateStatus(match.params.jobId, status);
  };

  const handleCloseJob = () => {
    doUpdateStatus(match.params.jobId, JobStatus.CLOSED);
    setShowConfirmCloseJobPopup(false);
  };

  if (isInitialLoading || isLoading || isUpdateJobPostingPending) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
        <Spinner
          animation="border"
          role="status"
          variant="info"
          style={{
            height: 30,
            width: 30,
            marginLeft: 20,
            marginTop: 10,
          }}
        />
      </div>
    );
  }

  return (
    <>
      <TabViewContainer
        title="Permanent Job Information"
        style={{
          width: '95%',
          maxWidth: 800,
          padding: 0,
          margin: 0,
        }}
      >
        <JobDetailsCard
          job={job}
          handleRecruiterChange={(_, email) => doUpdateRecruiter(match.params.jobId, email)}
          handleJobStatusChange={onJobStatusChange}
        />

        <GeneralInformationCard
          formValues={formValues}
          isEditing={formEdits.isEditGeneralJobInfo}
          job={job}
          handleChange={handleFormChange}
          onCancelClick={formEdits.controls.generalJobInfo.setOff}
          onEditClick={() => {
            formEdits.controls.generalJobInfo.setOn();
            formEdits.controls.compensationBenefits.setOff();
            formEdits.controls.schedule.setOff();
            formEdits.controls.jobDescription.setOff();
          }}
          onSaveClick={handleSaveClick}
        />

        <CompensationBenefitsCard
          formValues={formValues}
          isEditing={formEdits.isEditCompensationBenefits}
          job={job}
          handleChange={handleFormChange}
          onCancelClick={formEdits.controls.compensationBenefits.setOff}
          onEditClick={() => {
            formEdits.controls.generalJobInfo.setOff();
            formEdits.controls.compensationBenefits.setOn();
            formEdits.controls.schedule.setOff();
            formEdits.controls.jobDescription.setOff();
          }}
          onSaveClick={handleSaveClick}
        />

        <ScheduleInformationCard
          formValues={formValues}
          isEditing={formEdits.isEditSchedule}
          job={job}
          handleChange={handleFormChange}
          onCancelClick={formEdits.controls.schedule.setOff}
          onEditClick={() => {
            formEdits.controls.generalJobInfo.setOff();
            formEdits.controls.compensationBenefits.setOff();
            formEdits.controls.schedule.setOn();
            formEdits.controls.jobDescription.setOff();
          }}
          onSaveClick={handleSaveClick}
        />

        <JobDescriptionCard
          formValues={formValues}
          isEditing={formEdits.isEditJobDescription}
          job={job}
          handleChange={handleFormChange}
          onCancelClick={formEdits.controls.jobDescription.setOff}
          onEditClick={() => {
            formEdits.controls.generalJobInfo.setOff();
            formEdits.controls.compensationBenefits.setOff();
            formEdits.controls.schedule.setOff();
            formEdits.controls.jobDescription.setOn();
          }}
          onSaveClick={handleSaveClick}
        />
      </TabViewContainer>

      {showConfirmCloseJobPopup && (
        <ConfirmCloseJobPopup
          onConfirm={handleCloseJob}
          onClose={() => setShowConfirmCloseJobPopup(false)}
        />
      )}
    </>
  );
};
